import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group';
import { formatDataAmount } from '@/shared/utils/format-utils';
import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import {
  Bar,
  Legend,
  Tooltip as RechartsTooltip,
  LabelList,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BAR_TOP_RADIUS,
  CHART_OPACITY,
  REVERSE_MONTH_KEYS,
  monthKeyFromStartDate,
  MonthRangeOption,
  gbToTb,
  calendarPrevYm,
  compareMonthKeysAsc,
  TICK_FONT_SIZE,
} from '@/domains/global-dashboard/utils/chart-utils';
import { formatNumber } from '@/shared/utils/format-utils';
import {
  DashboardBarChart,
  DashboardBarGrid,
  DashboardBarPlotHoverCursor,
  DashboardChartViewport,
} from '@/domains/global-dashboard/components/GlobalDashboardBarChart';
import { barTopNumericLabelProps } from '@/domains/global-dashboard/components/dashboardChart';
import { DatapoolMonthSnap, datapoolSnapshotByYm } from '@/domains/global-dashboard/sections/consumption/data-consumption-utils';

/** One Recharts row per month — total consumed TB per market (stacked), keys match `account.name`. */
type DatapoolEvolutionChartRow = Record<string, string | number> & { month: string };

function buildEvolutionRowForMonth(
  ym: string,
  organizationSeries: { organization: OrganizationOverviewData; months: Map<string, DatapoolMonthSnap> }[],
): DatapoolEvolutionChartRow {
  const point: DatapoolEvolutionChartRow = { month: ym };
  for (const { organization, months } of organizationSeries) {
    const consumedGb = months.get(ym)?.consumedGb ?? 0;
    point[organization.name] = gbToTb(consumedGb);
  }
  return point;
}

/** Data consumption section component. */
export function DataConsumptionEvolutionChart({
  selectedOrganizations,
  accountChartColorById,
}: {
  selectedOrganizations: OrganizationOverviewData[];
  accountChartColorById?: Map<string, string>;
}) {
  const [monthRange, setMonthRange] = useState<MonthRangeOption>(12);

  const organizationSeries = useMemo(() => {
    return selectedOrganizations.map((organization) => ({
      organization,
      months: datapoolSnapshotByYm(organization),
    }));
  }, [selectedOrganizations]);

  const chartMonthTimeline = useMemo(() => {
    const keys = new Set<string>();
    for (const { months } of organizationSeries) {
      for (const ym of months.keys()) keys.add(ym);
    }
    return keys.size > 0
      ? [...keys].sort(compareMonthKeysAsc)
      : [...REVERSE_MONTH_KEYS];
  }, [organizationSeries]);

  const monthWindow = useMemo(() => {
    return chartMonthTimeline.slice(-monthRange);
  }, [monthRange, chartMonthTimeline]);

  const overallDatapoolEvolutionData = useMemo(() => {
    return chartMonthTimeline
      .map((ym) => buildEvolutionRowForMonth(ym, organizationSeries))
      .sort((a, b) => compareMonthKeysAsc(String(a.month), String(b.month)));
  }, [chartMonthTimeline, organizationSeries]);

  /** Evolution chart: rows for selected range only — derived from full `overallDatapoolEvolutionData`. */
  const selectedRangeConsumptionEvolutionData = useMemo(() => {
    const want = new Set(monthWindow);
    return overallDatapoolEvolutionData.filter((row) => want.has(String(row.month)));
  }, [overallDatapoolEvolutionData, monthWindow]);

  /** Single pass over responses; all views read from this. */
  const breakdownRows = useMemo(() => {
    const currentMonth = monthKeyFromStartDate(new Date().toISOString());

    type BreakdownRow = {
      organization: OrganizationOverviewData;
      consumedTb: number;
      unusedTb: number;
      contractedToUncappedTb: number;
      utilizationPct: number;
      contractedTb: number;
      cappedTb: number;
      uncappedTb: number;
      netChange: number;
    };

    const breakdownRows: BreakdownRow[] = [];
    for (const { organization, months } of organizationSeries) {
      const snap = months.get(currentMonth);
      if (!snap || snap.contractedGb <= 0) continue;

      const contractedTb = gbToTb(snap.contractedGb);
      const consumedTb = gbToTb(snap.consumedGb);
      const unusedTb = gbToTb(Math.max(0, snap.contractedGb - snap.consumedGb));
      const cappedTb = gbToTb(snap.cappedForecastGb);
      const uncappedTb = gbToTb(snap.uncappedForecastGb);
      const contractedToUncappedTb = gbToTb(Math.max(0, snap.uncappedForecastGb - snap.contractedGb));
      const utilizationPct = contractedTb > 0 ? (consumedTb / contractedTb) * 100 : 0;

      const prevYm = calendarPrevYm(currentMonth);
      const prevSnap = months.get(prevYm);
      if (!prevSnap) continue;

      const consumedTbLastMonth = gbToTb(prevSnap.consumedGb);
      const netChange = consumedTb - consumedTbLastMonth;

      breakdownRows.push({
        organization,
        consumedTb,
        unusedTb,
        contractedToUncappedTb,
        utilizationPct,
        contractedTb,
        cappedTb,
        uncappedTb,
        netChange,
      });
    }
    breakdownRows.sort((a, b) => b.utilizationPct - a.utilizationPct);

    return breakdownRows;
  }, [organizationSeries]);

  const datapoolMarketChartYMax = useMemo(() => {
    let m = 0;
    for (const r of breakdownRows) {
      m = Math.max(m, r.contractedTb, r.consumedTb + r.unusedTb, r.cappedTb, r.uncappedTb);
    }
    return formatNumber(m <= 0 ? 1 : m * 1.1);
  }, [breakdownRows]);

  return (
    <>
      {/* Data consumption evolution */}
      <Card>
        <CardHeader className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 justify-end items-center w-full">
            <div className="col-span-1">
              <CardTitle className="text-base">Data consumption evolution</CardTitle>
              <CardDescription>Data consumption evolution per month for the selected time range.</CardDescription>
            </div>
            <div className="col-span-1 flex items-center justify-end">
              {/* <p className="text-sm font-medium">Chart Date Range (months):</p> */}
              <ToggleGroup
                type="single"
                value={monthRange.toString()}
                onValueChange={(v) => v && setMonthRange(Number(v) as MonthRangeOption)}
                size="default"
                variant="outline"
              >
                <ToggleGroupItem value="1">1-Month</ToggleGroupItem>
                <ToggleGroupItem value="3">3-Months</ToggleGroupItem>
                <ToggleGroupItem value="6">6-Months</ToggleGroupItem>
                <ToggleGroupItem value="12">12-Months</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <DashboardChartViewport>
            <DashboardBarChart data={selectedRangeConsumptionEvolutionData} variant="timeSeries">
              <DashboardBarGrid />
              <XAxis dataKey="month" tick={{ fontSize: TICK_FONT_SIZE }} textAnchor="middle" />
              <YAxis
                tick={{ fontSize: TICK_FONT_SIZE }}
                domain={[0, datapoolMarketChartYMax]}
                scale="linear"
                tickFormatter={(value) => `${formatDataAmount(value)} TB`}
              />
              <RechartsTooltip formatter={(v: number) => `${formatDataAmount(v)} TB`} cursor={<DashboardBarPlotHoverCursor />} />
              <Legend />
              {selectedOrganizations.map((o) => {
                const c = accountChartColorById?.get(o.organizationId) ?? 'hsl(var(--primary))';
                return (
                  <Bar key={o.organizationId} dataKey={o.name} stackId={o.organizationId} fill={c} fillOpacity={CHART_OPACITY.marketBar} radius={BAR_TOP_RADIUS}>
                    <LabelList
                      {...barTopNumericLabelProps}
                      formatter={(value: unknown) => formatNumber(typeof value === 'number' ? value : Number(value))}
                    />
                  </Bar>
                );
              })}
            </DashboardBarChart>
          </DashboardChartViewport>
        </CardContent>
      </Card>
    </>
  );
}